import moment from "moment";
import { ElNotification, ElMessageBox } from "element-plus";
import {
  getRequest,
  postRequest,
  downloadWithSavePicker,
} from "~/apis/requests/useApiCall";
import { SmsStatus } from "~/types";
import { Pagination } from "~/types/pagination";
import { OutboundMessage } from "~/types/outbound_message";
import { TableDataWithPagination } from "~/types/generics/tableDataWithPagination";

export interface SmsBalance {
  sender_id: string;
  balance: number;
  credit: number;
  reserved: number;
  available: number;
}

export const useSmsHistoriesStore = definePiniaStore("smsHistoriesStore", {
  state: () => ({
    _tableData: [] as Array<OutboundMessage>,
    _loading: true,
    _filterData: {},
    _pagination: {} as Pagination,
    _statuses: [] as Array<SmsStatus>,
    multipleSelection: [],
    showMergeModal: false,
    showExcScenariosModal: false,
    showLogTimeModal: false,
    showTicketsFiltersModal: false,
    showBulkSmsModal: false,
    showRetrySmsModal: false,
    _retryTableData: [] as Array<OutboundMessage>,
    _retryLoading: false,

    _smsSummaryReport: { summary: {} as Record<string, any> },
    _smsSummaryReportFilter: {
      from_date: moment().format("YYYY-MM-DD"),
      to_date: moment().format("YYYY-MM-DD"),
      agent_id: null as number | null,
    } as Record<string, any>,
    _smsSummaryLoading: false,

    _smsBalance: {
      sender_id: "",
      balance: 0,
      credit: 0,
      reserved: 0,
      available: 0,
    } as SmsBalance,
    _smsBalanceLoading: false,
  }),
  actions: {
    toggleHistoriesFiltersModal() {
      this.showTicketsFiltersModal = !this.showTicketsFiltersModal;
    },
    openExcScenarioModal() {
      this.showExcScenariosModal = true;
    },
    openLogTimeModal() {
      this.showLogTimeModal = true;
    },
    closeExcScenarioModal() {
      this.showExcScenariosModal = false;
    },
    closeLogTimeModal() {
      this.showLogTimeModal = false;
    },
    openMergeModal() {
      this.showMergeModal = true;
    },
    closeMergeModal() {
      this.showMergeModal = false;
    },
    selectionChange(arr: any) {
      this.multipleSelection = arr;
    },
    setFilterData(filterData: object) {
      this._filterData = filterData;
    },

    async getSmsHistories(pageNo = 1, sortBy = "id", ascending = false) {
      this._loading = true;
      const sortDir = ascending ? "asc" : "desc";
      let url = `/sms?page=${pageNo}&sort_column=${sortBy}&dir=${sortDir}`;
      for (const [key, value] of Object.entries(this._filterData)) {
        if (value === undefined || value === null || value === "") continue;
        url += `&${key}=${value}`;
      }

      const { response, error } = await getRequest<
        TableDataWithPagination<OutboundMessage>
      >(url);
      if (!error) {
        const { data, ...pagination } = response.data;
        this.$state._tableData = data;
        this.$state._pagination = pagination;
      }
      this._loading = false;
    },
    async getRetryFailedSms(
      pageNo = 1,
      sortBy = "id",
      ascending = false,
      filterData?: Record<string, any>,
    ) {
      this._retryLoading = true;
      const sortDir = ascending ? "asc" : "desc";
      const filters = filterData ?? {};

      let url = `/sms?page=${pageNo}&sort_column=${sortBy}&dir=${sortDir}`;
      for (const [key, value] of Object.entries(filters)) {
        if (value === undefined || value === null || value === "") continue;
        url += `&${key}=${value}`;
      }

      const { response, error } = await getRequest<
        TableDataWithPagination<OutboundMessage>
      >(url);

      if (!error) {
        this.$state._retryTableData = response.data.data;
      }

      this._retryLoading = false;
    },
    async retrySms(requestData: { start_time: string; end_time: string }) {
      const { response, error } = await postRequest<any>(
        "/sms/retry",
        requestData,
      );

      if (!error) {
        ElNotification({
          type: "success",
          title: "Bulk SMS resending started",
          message: `Started for ${response?.data?.count ?? 0} unique numbers`,
        });

        return response?.data;
      } else {
        ElNotification({
          type: "error",
          title: "Retry Failed",
          message: error.response?.data?.message || error.message,
        });
        return null;
      }
    },
    async exportTable() {
      const sortDir = this._filterData.dir || "asc";
      const sortBy = this._filterData.sort_column || "id";
      let url = `/sms/export?sort_column=${sortBy}&dir=${sortDir}`;

      for (const [key, value] of Object.entries(this._filterData)) {
        if (!value) continue;
        url += `&${key}=${value}`;
      }

      const fileName = `sms_histories_export_${new Date().toISOString()}.xlsx`;

      await downloadWithSavePicker(url, fileName);
    },
    async bulkSend(file: File, template: string, skipProfileMapping = false) {
      const { response, error } = await postRequest<{ count: number }>(
        "/sms/sendInBulk",
        { file, template, skip_profile_mapping: skipProfileMapping ? 1 : 0 },
        true,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (!error) {
        return response.data;
      }
      if (error?.response?.data?.errors) {
        const errors = error.response.data.errors;

        const formattedMessages: string[] = [];

        for (const [key, messages] of Object.entries(errors)) {
          // convert row_2 → row 2
          const rowLabel = key.replace("_", " ");

          (messages as string[]).forEach((msg) => {
            formattedMessages.push(`${rowLabel} : ${msg}`);
          });
        }
        ElMessageBox.alert(
          formattedMessages.join("<br/>"),
          "Validation Failed",
          {
            confirmButtonText: "OK",
            dangerouslyUseHTMLString: true,
          },
        );
      } else {
        ElMessageBox.alert(
          error.response?.data?.message || error.message,
          "Bulk SMS Failed",
          {
            confirmButtonText: "OK",
          },
        );
      }

      return null;
    },

    async getSmsSummaryReport() {
      this._smsSummaryLoading = true;
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(this._smsSummaryReportFilter)) {
        if (value === null || value === undefined || value === "") continue;
        params.append(key, String(value));
      }
      const url = `reports/sms-summary?${params.toString()}`;

      const { response, error } = await getRequest<{
        summary: Record<string, any>;
      }>(url);

      if (!error) {
        this._smsSummaryReport = { summary: response.data?.summary ?? {} };
        ElNotification({ title: "Filter Applied", type: "success" });
      } else {
        ElMessageBox({
          title: "Error Fetching Report",
          message:
            error?.response?.data?.message || error?.response?.data?.error,
          type: "error",
        });
        this._smsSummaryReport = { summary: {} };
      }

      this._smsSummaryLoading = false;
    },

    async exportSmsSummaryReport() {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(this._smsSummaryReportFilter)) {
        if (value === null || value === undefined || value === "") continue;
        params.append(key, String(value));
      }
      const url = `reports/sms-summary/export?${params.toString()}`;
      await downloadWithSavePicker(
        url,
        `sms-summary-report-${moment().format("YYYY-MM-DD")}.xlsx`,
      );
    },

    setSmsSummaryReportFilter(data: Record<string, any>) {
      this._smsSummaryReportFilter = data;
    },

    async getSmsBalance() {
      this._smsBalanceLoading = true;
      const { response, error } = await getRequest<SmsBalance>("/sms/balance");
      if (!error) {
        this._smsBalance = response.data;
      } else {
        ElNotification({
          type: "error",
          title: "Failed to load SMS balance",
          message: error.response?.data?.message || error.message,
        });
      }
      this._smsBalanceLoading = false;
    },
  },
  getters: {
    showEditOptions(state) {
      return state.multipleSelection.length > 0;
    },
    tableData: (state) => state._tableData,
    retryTableData: (state) => state._retryTableData,
    statuses: (state) => {
      return state._statuses;
    },
    pagination: (state) => state._pagination,
    smsSummaryReport: (state) => state._smsSummaryReport,
    smsSummaryLoading: (state) => state._smsSummaryLoading,
    smsSummaryReportFilter: (state) => state._smsSummaryReportFilter,
    smsBalance: (state) => state._smsBalance,
    smsBalanceLoading: (state) => state._smsBalanceLoading,
  },
});
